import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { peekPostLoginPath } from "@/lib/market-return";

/**
 * Gatekeeper for routes that only make sense for a signed-OUT visitor - today
 * that's /auth and /auth/qr. This is the fix for "an already-authenticated
 * user who lands on /auth never gets sent to the feed": AuthPage itself never
 * looked at auth state at all (it just always rendered the login/signup
 * form), and /auth was never wrapped in ProtectedRoute either, so nothing in
 * the router ever redirected an authenticated visitor away from it - not
 * "sometimes," but every single time, including a direct link, a bookmark,
 * or the back button after logging in.
 *
 * Mirrors ProtectedRoute's own loading gate very deliberately, not just for
 * consistency: AuthProvider's silent-session-restore effect (the
 * `fetch(.../api/auth/refresh)` on mount) is genuinely async, so `user` is
 * still null for a brief moment even on a hard refresh where the person IS
 * authenticated. A naive check of only `user` (no loading gate) would let
 * that window render the real login form - and be genuinely interactive -
 * before the redirect below ever fires, which is exactly the intermittent
 * "sometimes an authenticated user can still access it" symptom rather than
 * a clean, consistent bug. Blocking on `loading` closes that window: nothing
 * renders (auth form OR redirect) until AuthProvider has actually settled.
 */
export default function PublicOnlyRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="flex h-screen items-center justify-center text-muted-foreground">Loading…</div>;
  }
  if (user) {
    // "/" rather than a hardcoded "/feed" or "/chat" - this way the
    // destination always matches whatever ProtectedRoute's own index route
    // currently treats as the canonical signed-in landing page (right now
    // that's /chat, see App.tsx) instead of this file hardcoding a second,
    // independent opinion that could quietly drift out of sync with it.
    return <Navigate to={peekPostLoginPath() ?? "/"} replace />;
  }
  return <Outlet />;
}
